import React, { useState, useMemo, useEffect } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useFilterContext } from "../head-bar/filter-context";
import { TagGroup } from "./group";
import { Cuisines } from "./cuisines";
import { CloseIcon } from "@/icons";
import Loading from "@/components/dc-loading";
import { Modal } from "antd";
import { observer } from "mobx-react-lite";
import "./desktop.less";
function arraysEqualUnordered(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // Create shallow copies and sort them
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  // Compare the sorted arrays element by element
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return false;
  }

  return true;
}
function objEqualUnordered(a, b) {
  if(!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal !== bVal) {
      return false;
    }
  }
  return true;
}
export const FiltersDialog = observer(({ type, title, close }) => {
  const {
    state,
    setState,
    fakeState,
    setFakeState,
    cleanFakeState,
    filterParams,
    loading,
    total,
    cleanFilter,
    setIsInFakeMode,
    bumpSearchTrigger,
    fakeRange,
    mapRange,
    setMapRange,
  } = useFilterContext();
  const { t } = useTranslation();
  const [isModalOpen, setOpen] = useState(true);
  const modalSize = useMemo(() => {
    return (type === "location" || type === "filters") ? 720 : 483;
  }, [type])

  useEffect(() => {
    // 弹窗展示时设置isInFakeMode为true
    setIsInFakeMode(true);

    // 组件卸载时（离开页面）设置isInFakeMode为false
    return () => {
      setIsInFakeMode(false);
    };
  }, [setIsInFakeMode]);

  const handleCancel = () => {
    setOpen(false);
    close();
    setIsInFakeMode(false);
  };
  const onOK = () => {
    // 对比fakeState和state, 如果更改了distance, district, landmarks, 则需要重置mapRange
    const locationChanged =
      fakeState.distance !== state.distance ||
      !objEqualUnordered(fakeRange, mapRange) ||
      !arraysEqualUnordered(fakeState["location.id"], state["location.id"]) ||
      !arraysEqualUnordered(fakeState["landmarks.id"], state["landmarks.id"]);
    setIsInFakeMode(false);
    setOpen(false);
    close();
    setState({ ...fakeState });
    setMapRange(locationChanged ? null : fakeRange);
    bumpSearchTrigger();
  };
  const toggle = (id, key) => {
    setFakeState((prev) => {
      const val = prev[key] || [];
      if (val === id) {
        return { [key]: "" };
      }
      return { [key]: id };
    });
  };

  const handleCleanFakeState = (type) => {
    cleanFakeState(type);
  };

  const okText = useMemo(() => {
    if (total > 0) {
      return t("Header.See {count}", { count: total });
    }
    return t("Header.No results");
  }, [total]);

  return (
    <Modal
      width={modalSize}
      visible={isModalOpen}
      onCancel={handleCancel}
      wrapClassName="desktop-filter-dialog"
      footer={null}
      zIndex={3000}
      closeIcon={<CloseIcon />}
    >
      <div className="desktop-filter-dialog-head">
        <div className="desktop-filter-dialog-head-title">{title}</div>
      </div>
      <div className="desktop-filter-dialog-body">
        {type === "location" && (
          <Location
            filterParams={filterParams}
            t={t}
            state={fakeState}
            toggle={toggle}
          />
        )}
        {type === "filters" && (
          <All
            filterParams={filterParams}
            t={t}
            state={fakeState}
            toggle={toggle}
          />
        )}
        {type === "cuisines" && (
          <Cuisines classNames={["desktop-filter-cuisines"]} />
        )}
      </div>
      <div className="desktop-filter-dialog-foot">
        <span className="ui-button" onClick={() => handleCleanFakeState(type)}>
          {t("Header.Clear all filters")}
        </span>
        <span
          className={cn("ui-button", { "ui-button--disabled": total == 0 })}
          type="primary"
          onClick={onOK}
        >
          {loading ? <Loading></Loading> : okText}
        </span>
      </div>
    </Modal>
  );
});

const All = ({ filterParams, t, state, toggle }) => {
  return (
    <>
      <TagGroup
        title={t("Header.Recommended")}
        list={filterParams.tags}
        name="tags.id"
        styles={{ marginTop: 0 }}
        showAll={true}
      />
      <div className="head-filter-group">
        <div className="head-filter-title">{t("Browse.price_range")}</div>
        <div className="head-filter-list">
          {filterParams.price_range.map((item, index) => {
            let val = [item.start_price, item.end_price].join(",");
            return (
              <div
                className={cn("filter__tag", {
                  "filter__tag--active": state.avg_price === `${val}`,
                })}
                key={index}
                onClick={() => toggle(val, "avg_price")}
              >
                {item.view_price}
              </div>
            );
          })}
        </div>
      </div>

      <div className="head-filter-group">
        <div className="head-filter-title">{t("Browse.distance")}</div>
        <div className="head-filter-list">
          {filterParams.distance.map((item, index) => {
            let val = item + "m";
            return (
              <div
                className={cn("filter__tag", {
                  "filter__tag--active": state.distance === `${val}`,
                })}
                key={index}
                onClick={() => toggle(val, "distance")}
              >
                {`< ${item}m`}
              </div>
            );
          })}
        </div>
      </div>
      <TagGroup
        title={t("Browse.district")}
        list={filterParams.locations}
        name="location.id"
        showAll={true}
      />
      <TagGroup
        title={t("Browse.landmarks")}
        list={filterParams.landmarks}
        name="landmarks.id"
        showAll={true}
      />
      <Cuisines />
      <TagGroup
        title={t("Browse.tags")}
        list={filterParams.extras}
        name="extras.id"
        showAll={true}
      />
    </>
  );
};
const Location = ({ filterParams, t, state, toggle }) => {
  return (
    <>
      <div className="head-filter-group" style={{ marginTop: 0 }}>
        <div className="head-filter-title">{t("Browse.distance")}</div>
        <div className="head-filter-list">
          {filterParams.distance.map((item, index) => {
            let val = item + "m";
            return (
              <div
                className={cn("filter__tag", {
                  "filter__tag--active": state.distance === `${val}`,
                })}
                key={index}
                onClick={() => toggle(val, "distance")}
              >
                {`< ${item}m`}
              </div>
            );
          })}
        </div>
      </div>
      <TagGroup
        title={t("Browse.district")}
        list={filterParams.locations}
        name="location.id"
        showAll={true}
      />
      <TagGroup
        title={t("Browse.landmarks")}
        list={filterParams.landmarks}
        name="landmarks.id"
        showAll={true}
      />
    </>
  );
};
